// ==================== ADMIN DASHBOARD SERVICE ====================

import { apiService } from './api';
import type { CommissionTotals } from './resellers.service';
import type { ApiResponse } from '../types';

// ==================== TYPES ====================

export interface DashboardStats {
  totalCustomers: number;
  customerChange: {
    value: number;
    type: 'positive' | 'negative' | 'neutral';
  };
  activeCustomers: number;
  pendingCustomers: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  subscriptionChange: {
    value: number;
    type: 'positive' | 'negative' | 'neutral';
  };
  monthlyRevenue: number;
  revenueChange: {
    value: number;
    type: 'positive' | 'negative' | 'neutral';
  };
  totalRevenue: number;
  mrr: number | null;
  mrrByCurrency: Array<{ currency: string; amount: number }>;
  resellerCommission: CommissionTotals;
}

export interface RecentCustomer {
  id: string;
  name: string;
  email: string;
  plan: string;
  status: string;
  createdAt: string;
}

export interface RecentPayment {
  id: string;
  organization: string;
  amount: number;
  type: string;
  createdAt: string;
}

export interface PlanDistribution {
  name: string;
  count: number;
  color: string;
}

export interface RevenueTrendItem {
  month: string;
  revenue: number;
}

export interface CustomerGrowthItem {
  month: string;
  customers: number;
}

export interface DashboardAnalytics {
  stats: DashboardStats;
  recentCustomers: RecentCustomer[];
  recentPayments: RecentPayment[];
  subscriptionsByPlan: PlanDistribution[];
  monthlyRevenueTrend: RevenueTrendItem[];
  customerGrowthTrend: CustomerGrowthItem[];
}

export interface ActivityItem {
  id: string;
  action: string;
  time: string;
  type: 'success' | 'info' | 'warning';
}

// ==================== SERVICE ====================

class DashboardService {
  /**
   * Get comprehensive dashboard analytics
   */
  async getAnalytics(): Promise<ApiResponse<DashboardAnalytics>> {
    return apiService.get<DashboardAnalytics>('/admin/dashboard');
  }

  /**
   * Get recent activity feed
   */
  async getRecentActivity(limit = 10): Promise<ApiResponse<ActivityItem[]>> {
    return apiService.get<ActivityItem[]>(`/admin/dashboard/activity?limit=${limit}`);
  }
}

export const dashboardService = new DashboardService();

